"use client";

import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import Image from "next/image";
import Link from "next/link";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import {
    Minus,
    Plus,
    ShoppingCart,
    MoveRight,
    ChevronLeft,
    ChevronRight,
} from "lucide-react";
import { useState, useMemo, useEffect } from "react";
import {
    Carousel,
    CarouselContent,
    CarouselItem,
    type CarouselApi,
} from "@/components/ui/carousel";
import { Product } from "@/types/product.types";
import { useCart } from "@/features/cart/hook/useCart";
import { getOptionPrice } from "@/lib/pricing";


interface QuickViewModalProps {
    product: Product | null;
    isOpen: boolean;
    onClose: () => void;
}

export const QuickViewModal = ({
    product,
    isOpen,
    onClose,
}: QuickViewModalProps) => {
    const [quantity, setQuantity] = useState(1);
    const [selectedWeight, setSelectedWeight] = useState<string>("");
    const [currentImageIndex, setCurrentImageIndex] = useState(0);
    const [api, setApi] = useState<CarouselApi>();
    const { addItem } = useCart();

    useEffect(() => {
        if (!api) return;

        const onSelect = () => {
            setCurrentImageIndex(api.selectedScrollSnap());
        };

        api.on("select", onSelect);
        return () => {
            api.off("select", onSelect);
        };
    }, [api]);

    useEffect(() => {
        setCurrentImageIndex(0);
        api?.scrollTo(0);
    }, [product?.id, api]);

    const safeImages = useMemo(() => {
        const images = product?.images?.map(img => img.url) || [];
        if (product?.image) images.unshift(product.image);
        const filtered = images.filter((img): img is string => !!img && img.trim() !== "");
        return filtered.length > 0 ? Array.from(new Set(filtered)) : ["/placeholder.png"];
    }, [product]);

    if (!product) return null;

    const weightAttribute = product?.attributes?.find(
        (attr) => (attr?.attribute?.options ?? []).length > 0
    );
    const weightOptions = weightAttribute?.attribute?.options ?? [];

    // Track selected option by ID — safe even when option.value is null (fixed-price options)
    const activeOptionId = selectedWeight || weightOptions?.[0]?.id || "";
    const option = weightOptions.find((o) => o.id === activeOptionId) || weightOptions[0];

    // Use salePrice as base (already has product-level discounts applied by backend)
    const effectiveBase = product.salePrice ? Number(product.salePrice) : Number(product.price || 0);
    const currentPrice = getOptionPrice(effectiveBase, option);

    const oldPrice = (() => {
        if (!product.originalPrice || !product.salePrice) return null;
        const calc = getOptionPrice(Number(product.originalPrice), option);
        if (calc === currentPrice) return null;
        return calc;
    })();

    const handleAddToCart = async () => {
        await addItem(product.id, option?.id || '', quantity);
        onClose();
    };



    return (
        <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
            <DialogContent className="max-w-[95vw] sm:max-w-140 p-0 bg-white border-none rounded-3xl overflow-hidden">
                {/* Image */}
                <div className="relative w-full h-87.5 bg-gray-50 flex items-center justify-center group overflow-hidden">
                    <Carousel
                        setApi={setApi}
                        className="w-full h-full"
                        opts={{
                            loop: true,
                            align: "start",
                        }}
                    >
                        <CarouselContent className="h-full">
                            {safeImages.map((img, i) => (
                                <CarouselItem key={i} className="relative w-full h-87.5">
                                    <Image
                                        src={img}
                                        alt={product.name}
                                        fill
                                        priority
                                        unoptimized
                                        className="object-contain p-4"
                                    />
                                </CarouselItem>
                            ))}
                        </CarouselContent>

                        {/* Arrows */}
                        {safeImages.length > 1 && (
                            <>
                                <div className="absolute inset-0 flex items-center justify-between px-4 opacity-0 group-hover:opacity-100 transition-opacity z-20 pointer-events-none">
                                    <button
                                        onClick={(e) => {
                                            e.preventDefault();
                                            api?.scrollPrev();
                                        }}
                                        className="h-10 w-10 bg-white/80 hover:bg-white rounded-full shadow-md flex items-center justify-center transition-all pointer-events-auto"
                                        aria-label="Image précédente"
                                    >
                                        <ChevronLeft className="h-5 w-5 text-gray-700" />
                                    </button>
                                    <button
                                        onClick={(e) => {
                                            e.preventDefault();
                                            api?.scrollNext();
                                        }}
                                        className="h-10 w-10 bg-white/80 hover:bg-white rounded-full shadow-md flex items-center justify-center transition-all pointer-events-auto"
                                        aria-label="Image suivante"
                                    >
                                        <ChevronRight className="h-5 w-5 text-gray-700" />
                                    </button>
                                </div>

                                {/* Image Counter Overlay */}
                                <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-1.5 px-3 py-1.5 bg-black/10 backdrop-blur-md rounded-full z-20">
                                    {safeImages.map((_, i) => (
                                        <div
                                            key={i}
                                            className={`w-1.5 h-1.5 rounded-full transition-all ${i === currentImageIndex ? "bg-white w-4" : "bg-white/50"
                                                }`}
                                        />
                                    ))}
                                </div>
                            </>
                        )}
                    </Carousel>
                </div>

                {/* Content */}
                <div className="flex flex-col gap-4 px-5 py-5 sm:px-6 sm:py-6">
                    {/* Title */}
                    <DialogHeader className="gap-1 text-left">
                        <div className="flex flex-col gap-0.5">
                            <Link href={`/products/${product.slug}`} className="hover:text-primary-700 transition-colors">
                                <DialogTitle className="text-2xl font-brastika text-gray-900 leading-tight">
                                    {product.name}
                                </DialogTitle>
                            </Link>
                            {product.subName && (
                                <p className="text-[10px] text-gray-400 font-bold uppercase tracking-widest -mt-0.5">
                                    {product.subName}
                                </p>
                            )}
                            {product.sku && (
                                <span className="text-[10px] uppercase tracking-widest text-primary-500 font-medium">
                                    SKU: {product.sku}
                                </span>
                            )}
                        </div>
                    </DialogHeader>

                    {/* Price + Controls */}
                    <div className="flex flex-wrap items-center gap-3">
                        <div className="flex items-end gap-2">
                            {oldPrice && (
                                <span className="line-through text-red-600 text-lg font-bold">
                                    {oldPrice.toLocaleString()} DA
                                </span>
                            )}
                            <span className="text-2xl font-bold text-primary-700">
                                {currentPrice.toLocaleString()} DA
                            </span>
                        </div>

                        {weightOptions.length > 0 && (
                            <Select
                                value={activeOptionId}
                                onValueChange={(value) => setSelectedWeight(value)}
                            >
                                <SelectTrigger className="px-4 py-2 border border-gray-300 rounded-lg text-sm w-auto h-10 min-w-[100px]">
                                    <SelectValue placeholder="Poids" />
                                </SelectTrigger>

                                <SelectContent align="start">
                                    {weightOptions.map((opt) => (
                                        <SelectItem key={opt.id} value={opt.id}>
                                            {opt.label}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        )}

                        <div className="flex items-center border border-gray-300 rounded-lg overflow-hidden">
                            <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => setQuantity(Math.max(1, quantity - 1))}
                            >
                                <Minus className="h-4 w-4" />
                            </Button>

                            <Input
                                readOnly
                                value={quantity}
                                className="w-12 text-center border-none focus-visible:ring-0"
                            />

                            <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => setQuantity(quantity + 1)}
                            >
                                <Plus className="h-4 w-4" />
                            </Button>
                        </div>
                    </div>

                    {/* Description */}
                    <div 
                        className="text-sm text-gray-600 leading-relaxed max-h-32 overflow-y-auto rich-text-content"
                        dangerouslySetInnerHTML={{ __html: product.descriptionHtml || product.description || "" }}
                    />

                    {/* CTA */}
                    <Button variant={"default"} className=" w-50 sm:w-37.5 h-10 rounded-lg flex items-center gap-2" onClick={handleAddToCart}>
                        <ShoppingCart className="h-5 w-5" />
                        Ajouter au panier
                    </Button>

                    {/* Footer */}
                    <Link href="/products" onClick={onClose} className="text-sm text-gray-700 flex items-center gap-2 w-fit hover:text-primary-700 transition-colors">
                        Plus de produits
                        <MoveRight className="h-4 w-4" />
                    </Link>
                </div>
            </DialogContent>
        </Dialog>
    );
};
